import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useSyncExternalStore,
} from 'react';
import type { EditorScreen } from '@/store/editorSlice';

export interface FormValuesContextValue {
  values: Record<string, string>;
  setValue: (blockId: string, value: string) => void;
  /**
   * WEB-3335 доработка (решение владельца 15.10, ночь): запись УМОЛЧАНИЯ —
   * то, что блок пишет сам при показе, а не покупатель. Значение уже есть —
   * не трогает; нет — ставит ключ перед первым явно записанным
   * (`writeDefault`). Явный выбор пишется только `setValue`.
   */
  setDefaultValue: (blockId: string, value: string) => void;
  /**
   * F1b: Clear all form values. Called on RESTART action to reset ANSWER
   * sources in EvalContext (D5 RATIFIED: RESTART clears ANSWER + VARIABLE).
   */
  resetValues: () => void;
  /**
   * WEB-2492: экраны квиза, которому принадлежит провайдер (задаёт страница
   * квиза). Нужны встроенному пейволлу, чтобы прочитать ответы квиза в форме
   * для правил (множественный выбор — список). Только чтение; у провайдера
   * пейволла и редактора поля нет.
   */
  screens?: EditorScreen[];
}

const FormValuesContext = createContext<FormValuesContextValue | null>(null);

interface FormValuesProviderProps {
  children: React.ReactNode;
  /**
   * WEB-525: seed values on mount — e.g. app-origin flow prefilling the email
   * block from `?email=`. Read once at mount (initial useState); later URL
   * changes do not re-seed, matching "user may correct" (PM 2026-07-07).
   */
  initialValues?: Record<string, string>;
  /** WEB-2492: см. `FormValuesContextValue.screens`. */
  screens?: EditorScreen[];
}

/**
 * WEB-1993 п.3 — узкая подписка на ОДНО значение.
 *
 * Общий контекст отдаёт весь словарь ответов: любой ответ покупателя менял
 * его целиком, и перерисовывался КАЖДЫЙ блок экрана, даже заголовок. React
 * тут ни при чём — так устроен контекст: подписчик перерисовывается на любое
 * изменение значения провайдера, и `React.memo` над блоком этого не отменяет.
 * Поэтому рядом с общим контекстом живёт второй, «магазинный»: стабильный
 * объект с чтением по ключу и подпиской. Блок, подписанный через
 * `useBlockFormValue(blockId)`, просыпается только на СВОЁ значение.
 *
 * Общий `useFormValues()` остаётся ровно тем же и для тех же потребителей
 * (визарды, сводка ответов, кошельки) — ничего из существующего не меняется.
 */
interface FormValuesStore {
  getValue: (blockId: string) => string;
  subscribe: (listener: () => void) => () => void;
  setValue: (blockId: string, value: string) => void;
  resetValues: () => void;
}

const FormValuesStoreContext = createContext<FormValuesStore | null>(null);

/**
 * WEB-3335 (деньги) — ЕДИНСТВЕННАЯ запись значения формы: ключ встаёт в
 * КОНЕЦ словаря, даже если уже был. Порядок ключей значений = порядок
 * последней записи, и лестница цены (`resolvePriceId.ts`) берёт по нему
 * тариф, который покупатель выбрал ПОСЛЕДНИМ в любом показанном ему
 * «Выборе тарифа» (критерий владельца 15.10). Прежняя запись разворотом
 * `{ ...prev, [id]: v }` оставляла перезаписанный ключ на старом месте.
 * Обе двери ЯВНОЙ записи (общий контекст и узкий магазин) идут сюда;
 * умолчание блока при показе — отдельной дверью `writeDefault` ниже.
 *
 * Граница: ключ, похожий на целое число («12»), JavaScript ставит вперёд
 * строковых при любом порядке записи. Идентификаторы блоков — UUID, такого
 * не бывает; самодельный id-число в порядке «последнего выбора» проиграет.
 */
export function writeLast(
  prev: Record<string, string>,
  blockId: string,
  value: string,
): Record<string, string> {
  const { [blockId]: _old, ...rest } = prev;
  return { ...rest, [blockId]: value };
}

/**
 * WEB-3335 доработка (деньги) — решение владельца 15.10 (ночь): «Тариф по
 * умолчанию не должен перебивать выбор, который покупатель сделал сам».
 * Запись умолчания блока при показе (например, «Выбор тарифа» при повторном
 * показе после возврата «назад»):
 * - значение у ключа уже есть — словарь не меняется (ни значение, ни место);
 * - нет — ключ встаёт ПЕРЕД первым ключом из `explicit` (записанным явно),
 *   а если явных нет — в конец.
 * Так «последний» в порядке ключей — последний ЯВНЫЙ выбор, а пока
 * покупатель ничего не выбирал сам — последнее показанное умолчание
 * (лестница цены, `findLastChosenSelection` в resolvePriceId.ts).
 */
export function writeDefault(
  prev: Record<string, string>,
  blockId: string,
  value: string,
  explicit: ReadonlySet<string>,
): Record<string, string> {
  if (Object.prototype.hasOwnProperty.call(prev, blockId)) return prev;
  const next: Record<string, string> = {};
  let placed = false;
  for (const key of Object.keys(prev)) {
    if (!placed && explicit.has(key)) {
      next[blockId] = value;
      placed = true;
    }
    next[key] = prev[key];
  }
  if (!placed) next[blockId] = value;
  return next;
}

export function FormValuesProvider({
  children,
  initialValues,
  screens,
}: FormValuesProviderProps) {
  const [values, setValues] = useState<Record<string, string>>(
    () => initialValues ?? {},
  );
  // WEB-3335 доработка: ключи, записанные ЯВНО (покупателем или
  // восстановленные из сохранённого прогресса), — умолчание встаёт перед ними.
  const explicitRef = useRef<Set<string>>(
    new Set(Object.keys(initialValues ?? {})),
  );

  // Зеркало значений для подписчиков: читается синхронно, в том числе из
  // getSnapshot во время отрисовки.
  const valuesRef = useRef(values);
  const listenersRef = useRef(new Set<() => void>());
  valuesRef.current = values;
  useEffect(() => {
    for (const listener of listenersRef.current) listener();
  }, [values]);

  // Обе двери ЯВНОЙ записи (узкий магазин и общий контекст) — одна функция.
  const store = useMemo<FormValuesStore>(
    () => ({
      getValue: (blockId) => valuesRef.current[blockId] ?? '',
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
      setValue: (blockId, value) => {
        explicitRef.current.add(blockId);
        setValues((prev) => writeLast(prev, blockId, value));
      },
      resetValues: () => {
        explicitRef.current = new Set();
        setValues({});
      },
    }),
    [],
  );

  const ctx = useMemo<FormValuesContextValue>(
    () => ({
      values,
      setValue: store.setValue,
      setDefaultValue: (blockId, value) =>
        setValues((prev) =>
          writeDefault(prev, blockId, value, explicitRef.current),
        ),
      resetValues: store.resetValues,
      screens,
    }),
    [values, screens, store],
  );

  return (
    <FormValuesStoreContext.Provider value={store}>
      <FormValuesContext.Provider value={ctx}>
        {children}
      </FormValuesContext.Provider>
    </FormValuesStoreContext.Provider>
  );
}

/**
 * Значение ОДНОГО блока + стабильный писатель. Вне провайдера (превью
 * редактора) ведёт себя как прежний заглушечный `useFormValues`: пустое
 * значение и запись в никуда — поле остаётся неуправляемым, падений нет.
 */
export function useBlockFormValue(blockId: string): {
  value: string;
  setValue: (blockId: string, value: string) => void;
} {
  const store = useContext(FormValuesStoreContext);
  const subscribe = useCallback(
    (listener: () => void) => store?.subscribe(listener) ?? (() => {}),
    [store],
  );
  const getSnapshot = useCallback(
    () => store?.getValue(blockId) ?? '',
    [store, blockId],
  );
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback(
    (id: string, next: string) => store?.setValue(id, next),
    [store],
  );
  return { value, setValue };
}

/**
 * Returns the current form values context. When called outside a provider —
 * e.g. inside the dashboard editor preview — returns a no-op stub so the
 * input still renders as an uncontrolled field without crashing.
 */
export function useFormValues(): FormValuesContextValue {
  const ctx = useContext(FormValuesContext);
  if (ctx) return ctx;
  return {
    values: {},
    setValue: () => {
      /* no-op outside provider (editor preview) */
    },
    setDefaultValue: () => {
      /* no-op outside provider (editor preview) */
    },
    resetValues: () => {
      /* no-op outside provider */
    },
  };
}
